import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Loader2, Send, Users, Building, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBulkSmsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const api = useApi();
  const [clinics, setClinics] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Selection state
  const [selectionMode, setSelectionMode] = useState('all'); // 'all', 'individual', 'by_clinic'
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState('');
  
  // Message state
  const [message, setMessage] = useState('');
  const [encoding, setEncoding] = useState('plain');
  const [campaignName, setCampaignName] = useState('');
  
  // Preview dialog
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    loadData();
    // eslint-disable-next-line
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load clinics using useApi hook
      const clinicsData = await api.get('/clinics');
      setClinics(clinicsData);
      
      // Load all clinic admins
      const adminsData = await api.get('/admin/clinic-admins');
      setAdminUsers(adminsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = (adminId) => {
    setSelectedAdmins(prev => 
      prev.includes(adminId) 
        ? prev.filter(id => id !== adminId)
        : [...prev, adminId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAdmins.length === getFilteredAdmins().length) {
      setSelectedAdmins([]);
    } else {
      setSelectedAdmins(getFilteredAdmins().map(admin => admin.id));
    }
  };

  const getFilteredAdmins = () => {
    if (selectionMode === 'by_clinic' && selectedClinic) {
      return adminUsers.filter(admin => admin.clinic_id === selectedClinic);
    }
    return adminUsers;
  };

  const getSelectedAdminsList = () => {
    if (selectionMode === 'all') {
      return adminUsers;
    }
    return adminUsers.filter(admin => selectedAdmins.includes(admin.id));
  };

  const calculateSmsCount = () => {
    if (!message) return 0;
    const charLimit = encoding === 'plain' ? 160 : 70;
    return Math.ceil(message.length / charLimit);
  };

  const handlePreview = () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (!campaignName.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (selectionMode !== 'all' && selectedAdmins.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    setShowPreview(true);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const recipients = getSelectedAdminsList();
      
      const data = await api.post('/sms/admin-bulk', {
        admin_ids: selectionMode === 'all' ? 'all' : selectedAdmins,
        content: message,
        encoding: encoding,
        campaign_name: campaignName
      });

      toast.success(`SMS sent successfully! Sent: ${data.sent_count}, Failed: ${data.failed_count || 0}`);
      setMessage('');
      setCampaignName('');
      setSelectedAdmins([]);
      setShowConfirm(false);
      setShowPreview(false);
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const recipientCount = selectionMode === 'all' ? adminUsers.length : selectedAdmins.length;
  const smsCountPerRecipient = calculateSmsCount();
  const totalCost = recipientCount * smsCountPerRecipient;

  if (user?.role !== 'superadmin') {
    return null;
  }
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>Admin Bulk SMS</h1>
          <p className="text-gray-600 mt-1">Send SMS to clinic administrators</p>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recipient Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Select Recipients
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Selection Mode</Label>
              <Select value={selectionMode} onValueChange={setSelectionMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clinic Admins</SelectItem>
                  <SelectItem value="individual">Select Individual Admins</SelectItem>
                  <SelectItem value="by_clinic">Select by Clinic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectionMode === 'by_clinic' && (
              <div className="space-y-2">
                <Label>Select Clinic</Label>
                <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose clinic" />
                  </SelectTrigger>
                  <SelectContent>
                    {clinics.map(clinic => (
                      <SelectItem key={clinic.id} value={clinic.id}>
                        {clinic.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectionMode !== 'all' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Administrators ({recipientCount} selected)</Label>
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    {selectedAdmins.length === getFilteredAdmins().length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="max-h-80 overflow-y-auto border rounded-lg p-4 space-y-2">
                  {loading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : getFilteredAdmins().length === 0 ? (
                    <p className="text-gray-500 text-center py-4">
                      {selectionMode === 'by_clinic' ? 'No admins found for selected clinic' : 'No clinic admins found'}
                    </p>
                  ) : (
                    getFilteredAdmins().map(admin => (
                      <div key={admin.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
                        <Checkbox
                          checked={selectedAdmins.includes(admin.id)}
                          onCheckedChange={() => handleToggleAdmin(admin.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{admin.full_name || admin.email}</p>
                          <p className="text-sm text-gray-500">{admin.email}</p>
                          {admin.clinic_name && (
                            <p className="text-xs text-gray-400">
                              <Building size={12} className="inline mr-1" />
                              {admin.clinic_name}
                            </p>
                          )}
                        </div>
                        {admin.phone && (
                          <Badge variant="outline">{admin.phone}</Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {selectionMode === 'all' && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <Users size={16} className="inline mr-2" />
                  All {adminUsers.length} clinic administrators will receive this SMS
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Composer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle size={20} />
              Compose Message
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="campaignName">Campaign Name</Label>
              <Input
                id="campaignName"
                placeholder="e.g., Monthly Offer, System Update"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="encoding">Encoding</Label>
              <Select value={encoding} onValueChange={setEncoding}>
                <SelectTrigger id="encoding">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plain">Normal Text (160 chars/SMS)</SelectItem>
                  <SelectItem value="unicode">Unicode/Nepali (70 chars/SMS)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="message">Message</Label>
                <span className="text-sm text-gray-500">
                  {message.length} / {encoding === 'plain' ? 160 : 70} chars
                </span>
              </div>
              <Textarea
                id="message"
                placeholder="Enter your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">SMS Count</p>
                <p className="text-2xl font-bold">{smsCountPerRecipient}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Cost</p>
                <p className="text-2xl font-bold text-purple-600">{totalCost} credits</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Recipients</p>
                <p className="text-2xl font-bold">{recipientCount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Per Recipient</p>
                <p className="text-2xl font-bold">{smsCountPerRecipient} credits</p>
              </div>
            </div>

            <Button 
              onClick={handlePreview} 
              className="w-full" 
              style={{ backgroundColor: "#652D90" }}
              disabled={loading}
            >
              <Send size={16} className="mr-2" />
              Preview & Send
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview SMS Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Campaign Name</Label>
              <p className="text-lg font-medium">{campaignName}</p>
            </div>
            <div>
              <Label>Message</Label>
              <div className="p-4 bg-gray-50 rounded-lg whitespace-pre-wrap">
                {message}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Recipients</Label>
                <p className="text-xl font-bold">{recipientCount} admins</p>
              </div>
              <div>
                <Label>Total Cost</Label>
                <p className="text-xl font-bold text-purple-600">{totalCost} credits</p>
              </div>
            </div>
            <div>
              <Label>Recipient List</Label>
              <div className="max-h-40 overflow-y-auto border rounded p-2 mt-2">
                {getSelectedAdminsList().map(admin => (
                  <div key={admin.id} className="text-sm py-1">
                    {admin.full_name || admin.email} - {admin.phone || 'No phone'}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => { setShowPreview(false); setShowConfirm(true); }}
              style={{ backgroundColor: "#652D90" }}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm SMS Send</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-700">
              You are about to send SMS to <strong>{recipientCount} clinic administrators</strong>.
            </p>
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ This action cannot be undone. {totalCost} credits will be deducted from the system.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={sending}>
              Cancel
            </Button>
            <Button 
              onClick={handleSend}
              style={{ backgroundColor: "#652D90" }}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Confirm & Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
